import React, { forwardRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import hopeLogo from 'images/pga-hope-logo.svg'
import { formatVerifiedDate, getVerificationState, isLongName } from './utils'

const CARD_INK = '#111111'
const PGA_NAVY = '#00234B'
const VERIFIED_GREEN = '#2E7D32'
const FRESHNESS_TICK_MS = 30 * 1000

const useStyles = makeStyles({
  card: {
    // Everything inside is sized in em so the card scales as one piece. The
    // font tracks the card width (viewport minus 1rem of padding per side) so
    // the layout wraps the same way from 375px up to the 480px maximum.
    fontSize: 'clamp(12px, calc((100vw - 2rem) / 30), 16px)',
    display: 'flex',
    width: '100%',
    maxWidth: 480,
    aspectRatio: '1.586',
    borderRadius: '1.1em',
    // `clip` keeps min-height: auto, so the card grows past its aspect ratio
    // instead of cutting off content when a long name wraps on a narrow screen.
    // JSS must emit `hidden` as a fallback declaration: an array value renders
    // as "hidden, clip", which is invalid and leaves the corners unclipped.
    fallbacks: { overflow: 'hidden' },
    overflow: 'clip',
    backgroundColor: '#FFFFFF',
    color: CARD_INK,
    border: '1px solid rgba(0, 35, 75, 0.14)',
    boxShadow:
      '0 0.05em 0.1em rgba(0, 17, 38, 0.10), 0 0.5em 1.2em -0.2em rgba(0, 17, 38, 0.22)',
    boxSizing: 'border-box',
    '@media (forced-colors: active)': {
      border: '1px solid CanvasText',
    },
  },
  main: {
    flex: '1.6 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '1.3em 1.3em 1.15em',
  },
  verified: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.55em',
    marginBottom: '0.7em',
    fontSize: '0.78em',
    lineHeight: 1.2,
    fontWeight: 600,
    letterSpacing: '0.01em',
    textTransform: 'none',
    color: PGA_NAVY,
  },
  verifiedDot: {
    flexShrink: 0,
    width: '0.65em',
    height: '0.65em',
    borderRadius: '50%',
    backgroundColor: VERIFIED_GREEN,
    animation: '$pulse 2.4s ease-out infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  staleDot: {
    flexShrink: 0,
    width: '0.65em',
    height: '0.65em',
    borderRadius: '50%',
    backgroundColor: '#8A94A3',
  },
  '@keyframes pulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(46, 125, 50, 0.45)' },
    '70%, 100%': { boxShadow: '0 0 0 0.5em rgba(46, 125, 50, 0)' },
  },
  time: {
    fontVariantNumeric: 'tabular-nums',
  },
  name: {
    fontSize: '1.6em',
    fontWeight: 700,
    lineHeight: 1.12,
    letterSpacing: '-0.01em',
    textWrap: 'balance',
    overflowWrap: 'anywhere',
  },
  nameLong: {
    fontSize: '1.3em',
  },
  detail: {
    marginTop: '0.1em',
    fontSize: '1em',
    lineHeight: 1.25,
    color: '#3A4452',
  },
  detailFirst: {
    marginTop: '0.4em',
  },
  logo: {
    marginTop: 'auto',
    width: '9.5em',
    maxWidth: '100%',
    flexShrink: 0,
    height: 'auto',
    display: 'block',
  },
  side: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'center',
    padding: '1.36em 0.6em 1.15em',
    backgroundColor: PGA_NAVY,
    backgroundImage:
      'linear-gradient(180deg, #002A57 0%, #00234B 45%, #001A38 100%)',
    color: '#FFFFFF',
    '@media (forced-colors: active)': {
      borderLeft: '1px solid CanvasText',
    },
  },
  year: {
    fontSize: '1.35em',
    fontWeight: 300,
    lineHeight: 1.2,
    letterSpacing: '0.04em',
  },
  graduate: {
    marginTop: '0.1em',
    fontSize: '1.2em',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    '&::after': {
      content: '""',
      display: 'block',
      width: '2.2em',
      height: 1,
      margin: '0.8em auto 0',
      backgroundColor: 'rgba(255, 255, 255, 0.35)',
    },
  },
  hashtag: {
    marginTop: 'auto',
    paddingTop: '0.8em',
    fontSize: '0.95em',
    fontWeight: 500,
    letterSpacing: '0.04em',
  },
})

// Re-evaluates freshness while the card stays open, so a card left on screen
// flips to "Last verified" instead of claiming to be current forever.
const useNow = () => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), FRESHNESS_TICK_MS)
    return () => clearInterval(id)
  }, [])
  return now
}

const VerifiedLine = ({ verifiedOn, verificationFailed, classes }) => {
  const now = useNow()
  const state = getVerificationState({
    verifiedOn,
    failed: verificationFailed,
    now,
  })
  if (!state) return null

  const isFresh = state === 'fresh'
  return (
    <Typography component="p" className={classes.verified} data-card-verified>
      <span
        className={isFresh ? classes.verifiedDot : classes.staleDot}
        aria-hidden="true"
      />
      <span>
        {isFresh ? 'Verified · ' : 'Last verified '}
        <time dateTime={verifiedOn.toISOString()} className={classes.time}>
          {formatVerifiedDate(verifiedOn)}
        </time>
      </span>
    </Typography>
  )
}

VerifiedLine.propTypes = {
  verifiedOn: PropTypes.instanceOf(Date),
  verificationFailed: PropTypes.bool,
  classes: PropTypes.object.isRequired,
}

const GraduateCard = forwardRef(
  (
    {
      name,
      serviceBranch,
      sectionName,
      graduationYear,
      verifiedOn,
      verificationFailed,
    },
    ref,
  ) => {
    const classes = useStyles()
    const details = [serviceBranch, sectionName].filter(Boolean)

    return (
      <Box
        ref={ref}
        role="group"
        aria-label={`PGA HOPE graduate card, ${name}`}
        className={classes.card}
      >
        <Box className={classes.main}>
          <VerifiedLine
            verifiedOn={verifiedOn}
            verificationFailed={verificationFailed}
            classes={classes}
          />
          <Typography
            component="p"
            className={clsx(classes.name, isLongName(name) && classes.nameLong)}
          >
            {name}
          </Typography>
          {details.map((detail, index) => (
            <Typography
              key={index}
              component="p"
              className={clsx(
                classes.detail,
                index === 0 && classes.detailFirst,
              )}
            >
              {detail}
            </Typography>
          ))}
          <img src={hopeLogo} alt="PGA HOPE" className={classes.logo} />
        </Box>
        <Box className={classes.side}>
          {graduationYear ? (
            <Typography component="p" className={classes.year}>
              {graduationYear}
            </Typography>
          ) : (
            <Typography
              component="p"
              className={classes.year}
              aria-hidden="true"
            >
              &nbsp;
            </Typography>
          )}
          <Typography component="p" className={classes.graduate}>
            Graduate
          </Typography>
          <Typography component="p" className={classes.hashtag}>
            #PGAHOPE
          </Typography>
        </Box>
      </Box>
    )
  },
)

GraduateCard.displayName = 'GraduateCard'

GraduateCard.propTypes = {
  name: PropTypes.string.isRequired,
  serviceBranch: PropTypes.string,
  sectionName: PropTypes.string,
  graduationYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  verifiedOn: PropTypes.instanceOf(Date),
  verificationFailed: PropTypes.bool,
}

export default GraduateCard
